import { generateIndustrySkill } from "@/api/industries";
import { generatePolicyTypeSkill } from "@/api/policy-types";
import { getSkill } from "@/api/skills";
import { ISkillMinimal } from "@/api/skills/skills.types";
import { ConfirmationDialog } from "@/components/shared/confirmation-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    ArrowUpRight,
    CircleCheck,
    RefreshCw,
    Sparkles,
    TriangleAlert,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

type EntityType = "industry" | "policy_type";

interface LinkedSkillSectionProps {
    entityId: number;
    entityType: EntityType;
    entityName: string;
    skill: ISkillMinimal | null | undefined;
    /** Query keys to invalidate after generation */
    invalidateKeys: string[][];
}

const ENTITY_LABELS: Record<EntityType, string> = {
    industry: "industry",
    policy_type: "policy type",
};

export const LinkedSkillSection = ({
    entityId,
    entityType,
    entityName,
    skill,
    invalidateKeys,
}: LinkedSkillSectionProps) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [justGenerated, setJustGenerated] = useState(false);
    const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
    const glowTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        return () => clearTimeout(glowTimerRef.current);
    }, []);

    // Fetch full skill to get status (active/inactive)
    const { data: fullSkill } = useQuery({
        queryKey: ["admin-skill-status", skill?.id],
        queryFn: () => getSkill(skill!.id),
        enabled: !!skill?.id,
        staleTime: 30_000,
    });

    const isActive = fullSkill?.status === "active";
    const isLinked = !!skill;

    const [isGenerating, setIsGenerating] = useState(false);

    const generateFn =
        entityType === "industry"
            ? generateIndustrySkill
            : generatePolicyTypeSkill;

    const { mutate: generate, isPending } = useMutation({
        mutationFn: () => generateFn(entityId),
        onMutate: () => setIsGenerating(true),
        onSuccess: async () => {
            await Promise.all([
                ...invalidateKeys.map((key) =>
                    queryClient.invalidateQueries({ queryKey: key })
                ),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skill-status"],
                }),
            ]);
            setIsGenerating(false);
            enqueueSnackbar("Skill generated successfully", {
                variant: "success",
            });
            setJustGenerated(true);
            clearTimeout(glowTimerRef.current);
            glowTimerRef.current = setTimeout(
                () => setJustGenerated(false),
                4000
            );
        },
        onError: (error: AxiosError<{ detail?: string }>) => {
            setIsGenerating(false);
            const detail =
                error.response?.data?.detail ||
                "Failed to generate skill. Please try again.";
            enqueueSnackbar(detail, { variant: "error" });
        },
    });

    const handleNavigateToSkill = useCallback(() => {
        if (skill) {
            navigate(`/admin/skills/${skill.id}/details`);
        }
    }, [navigate, skill]);

    const label = ENTITY_LABELS[entityType];

    // --- Generating state ---
    if (isGenerating) {
        return (
            <SkillCard generating>
                <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10">
                        <Spinner className="size-4 text-indigo-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <Skeleton className="mb-1.5 h-4 w-48" />
                        <Skeleton className="h-3 w-32" />
                    </div>
                </div>
                <p className="text-muted-foreground mt-3 text-xs">
                    Generating domain expertise for{" "}
                    <span className="text-foreground font-medium">
                        {entityName}
                    </span>
                    &hellip; This usually takes 1-2 minutes.
                </p>
            </SkillCard>
        );
    }

    // --- Empty state ---
    if (!isLinked) {
        return (
            <SkillCard>
                <div className="flex items-start gap-3">
                    <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
                        <Sparkles className="text-muted-foreground size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-foreground text-sm font-medium">
                            No skill linked
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                            Generate domain expertise to enhance AI analysis for
                            this {label}. The skill will provide specialized
                            context for contract review and chat.
                        </p>
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => generate()}
                        disabled={isPending}
                    >
                        <Sparkles data-icon="inline-start" />
                        Generate Skill
                    </Button>
                </div>
            </SkillCard>
        );
    }

    // --- Linked state (active or needs review) ---
    return (
        <SkillCard success={justGenerated}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    <div
                        className={cn(
                            "flex size-9 shrink-0 items-center justify-center rounded-lg",
                            isActive ? "bg-emerald-500/10" : "bg-amber-500/10"
                        )}
                    >
                        {isActive ? (
                            <CircleCheck className="size-4 text-emerald-600" />
                        ) : (
                            <TriangleAlert className="size-4 text-amber-600" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleNavigateToSkill}
                                className="text-foreground truncate text-sm font-medium underline-offset-2 hover:underline"
                            >
                                {skill.name}
                            </button>
                            <Badge
                                className={cn(
                                    "shrink-0",
                                    isActive
                                        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                        : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                )}
                            >
                                {isActive ? "Active" : "Review Required"}
                            </Badge>
                        </div>
                        {!isActive && (
                            <p className="text-muted-foreground mt-0.5 text-xs">
                                This skill needs to be reviewed and published
                                before it will be used in AI analysis.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNavigateToSkill}
                >
                    {isActive ? "View" : "Review"} Skill
                    <ArrowUpRight data-icon="inline-end" />
                </Button>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmingRegenerate(true)}
                    disabled={isPending}
                >
                    <RefreshCw data-icon="inline-start" />
                    Regenerate
                </Button>
            </div>

            <ConfirmationDialog
                open={confirmingRegenerate}
                onOpenChange={setConfirmingRegenerate}
                title="Regenerate skill?"
                description={
                    isActive
                        ? "This will replace the current skill content and unpublish it. You'll need to review and republish before it's used in AI analysis."
                        : "This will replace the current skill content. You'll need to review and publish before it's used in AI analysis."
                }
                onConfirm={() => generate()}
                variant={isActive ? "destructive" : "default"}
                confirmLabel="Regenerate"
            />
        </SkillCard>
    );
};

// --- Internal card wrapper ---

interface SkillCardProps {
    children: React.ReactNode;
    generating?: boolean;
    success?: boolean;
}

const SkillCard = ({
    children,
    generating = false,
    success = false,
}: SkillCardProps) => (
    <div
        className={cn(
            "bg-card rounded-lg border p-4 transition-all duration-300",
            generating && "border-indigo-500/30",
            success &&
                "border-emerald-500/30 shadow-[0_0_12px_-3px_rgba(16,185,129,0.2)]",
            !generating && !success && "border-border"
        )}
    >
        <div className="mb-2 flex items-center gap-1.5">
            <Sparkles className="text-muted-foreground size-3.5" />
            <span className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                AI Skill
            </span>
        </div>
        {children}
    </div>
);
